/* jshint esversion: 6 */
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { MyContext } from "../../Context/MyProvider";
import { showToast } from "../../showTost";
import { readStoredObject } from "../../utils/storage";
import V1CashfreeCheckout from "../../Payment/V1CashfreeCheckout";
import { RiMapPinLine, RiCrosshairLine, RiRefreshLine, RiCloseLine } from "react-icons/ri";

const HYDERABAD = { lat: 17.479, lng: 78.588 };

function formatDateLabel(value) {
  const s = String(value || "").slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (!y) return s;
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}

const emptyForm = { name: "", phone: "", house: "", street: "", landmark: "", saveAs: "Home" };

export default function PharmacyCheckout() {
  const { ApiBaseUrl, mapKey, setIsLoginShow } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoaded } = useLoadScript({ googleMapsApiKey: mapKey || "" });

  const [cart, setCart] = useState([]);
  const [slot, setSlot] = useState(null);
  const [ready, setReady] = useState(false);
  const [coupon, setCoupon] = useState("WELCOME10");
  const [onlineFlow, setOnlineFlow] = useState(false);

  // Address state — map pin is the source of truth for lat/lng.
  const [pin, setPin] = useState(HYDERABAD);
  const [form, setForm] = useState(emptyForm);
  const [geoLoading, setGeoLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(true);
  const addressCardRef = useRef(null);

  const uid = secureLocalStorage.getItem("Userid");

  useEffect(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem("pharmacy_cart") || "[]");
      if (!c.length) {
        navigate("/pharmacy", { replace: true });
        return;
      }
      setCart(c);

      const fromState = location.state?.slot;
      const fromStore = JSON.parse(sessionStorage.getItem("pharmacy_slot") || "null");
      const s = fromState || (fromStore && fromStore.delivery_slot ? fromStore : null);

      if (!s) {
        showToast({ title: "Choose a delivery slot first", id: "warning" });
        navigate("/pharmacy/slots", { replace: true });
        return;
      }
      sessionStorage.setItem("pharmacy_slot", JSON.stringify(s));
      setSlot(s);
      setReady(true);
    } catch (_) {
      navigate("/pharmacy", { replace: true });
    }
  }, [location.state, navigate]);

  // Prefill contact + pin, then load saved addresses.
  useEffect(() => {
    if (!ready) return;
    const login = readStoredObject("Loginuser") || {};
    setForm((f) => ({
      ...f,
      name: f.name || login.name || "",
      phone: f.phone || [login.country_code, login.phone].filter(Boolean).join(" ") || login.phone || "",
    }));
    const lat = Number(secureLocalStorage.getItem("lat"));
    const lon = Number(secureLocalStorage.getItem("lon"));
    if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0) setPin({ lat, lng: lon });

    if (uid) {
      axios.post(`${ApiBaseUrl}/customer/address_list`, { id: uid })
        .then((res) => {
          const list = res.data.address_list || [];
          setSavedAddresses(list);
          if (list.length) {
            const first = list[0];
            setSelectedSavedId(first.id);
            setShowNewForm(false);
            applySavedAddress(first);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applySavedAddress = (a) => {
    setForm({
      name: a.name || form.name || "",
      phone: a.phone ? `${a.country_code || ""} ${a.phone}`.trim() : form.phone || "",
      house: a.house_no || "",
      street: a.address || a.google_address || "",
      landmark: a.landmark || "",
      saveAs: a.address_as || "Home",
    });
    const lat = Number(a.latitude);
    const lng = Number(a.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0) setPin({ lat, lng });
  };

  const reverseGeocode = async (lat, lng) => {
    if (!window.google?.maps?.Geocoder) return "";
    return new Promise((resolve) => {
      new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (results, status) => {
        resolve(status === "OK" && results?.[0] ? results[0].formatted_address : "");
      });
    });
  };

  // Move pin → auto-fill the street field (patient can still edit it).
  const movePin = async (lat, lng, autofill = true) => {
    setPin({ lat, lng });
    if (!autofill) return;
    setGeoLoading(true);
    try {
      const addr = await reverseGeocode(lat, lng);
      if (addr) setForm((f) => ({ ...f, street: f.street || addr }));
    } finally {
      setGeoLoading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      showToast({ title: "Geolocation not supported", id: "warning" });
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setPin({ lat, lng });
        const addr = await reverseGeocode(lat, lng);
        setForm((f) => ({ ...f, street: addr || f.street }));
        setGeoLoading(false);
      },
      () => {
        setGeoLoading(false);
        showToast({ title: "Could not get your location", id: "error" });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const saveAddressToAccount = async () => {
    if (!uid) {
      setIsLoginShow(true);
      return;
    }
    if (!form.street.trim() || !form.phone.trim()) {
      showToast({ title: "Enter address and phone first", id: "warning" });
      return;
    }
    try {
      const res = await axios.post(`${ApiBaseUrl}/customer/add_address`, {
        id: uid,
        house_no: form.house,
        address: form.street,
        landmark: form.landmark,
        address_as: form.saveAs,
        country_code: "",
        phone: form.phone,
        latitude: pin.lat,
        longitude: pin.lng,
        google_address: form.street,
        instruction: "",
      });
      if (res.data.ResponseCode === 200) {
        showToast({ title: "Address saved", id: "success" });
        const listRes = await axios.post(`${ApiBaseUrl}/customer/address_list`, { id: uid });
        setSavedAddresses(listRes.data.address_list || []);
      }
    } catch (_) {
      showToast({ title: "Could not save address", id: "error" });
    }
  };

  const composedAddress = [form.house, form.street, form.landmark].filter((s) => s && s.trim()).join(", ");

  const validate = () => {
    if (!form.street.trim()) {
      showToast({ title: "Enter your delivery address", id: "warning" });
      addressCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return false;
    }
    if (form.phone.replace(/\D/g, "").length < 6) {
      showToast({ title: "Enter a valid phone number", id: "warning" });
      addressCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return false;
    }
    return true;
  };

  if (!ready) {
    return (
      <div className="width-container py-16 text-center text-gray-500">
        Loading checkout…
      </div>
    );
  }

  const totalQty = cart.reduce((sum, x) => sum + (x.quantity || 0), 0);
  const rxCount = cart.filter((x) => x.requires_prescription).length;

  const userLoc = () => ({
    latitude: pin.lat,
    longitude: pin.lng,
    address: composedAddress || form.street,
  });

  const startOnline = () => {
    if (!secureLocalStorage.getItem("Userid")) {
      setIsLoginShow(true);
      showToast({ title: "Please log in to complete order", id: "warning" });
      return;
    }
    if (!slot) {
      navigate("/pharmacy/slots", { replace: true });
      return;
    }
    if (!validate()) return;
    setOnlineFlow(true);
  };

  const createOnlineCheckout = async () => {
    const id = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ApiBaseUrl}/api/v1/pharmacy/orders/checkout`, {
      id,
      items: cart.map((c) => ({ medicine_id: c.medicine_id, quantity: c.quantity })),
      user_location: userLoc(),
      delivery_slot: slot?.delivery_slot,
      delivery_date: slot?.delivery_date,
      coupon_code: coupon || undefined,
    });
    if (!res.data.success) throw new Error(res.data.error_message || "Checkout failed");
    const c = res.data.checkout;
    return { payment_session_id: c.payment_session_id, cashfree_order_id: c.cashfree_order_id, ref: c.order_reference, environment: c.environment };
  };

  const confirmOnline = async ({ cashfree_order_id, ref }) => {
    const id = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ApiBaseUrl}/api/v1/pharmacy/orders/${ref}/confirm`, {
      id,
      cashfree_order_id,
    });
    if (!res.data.success) throw new Error(res.data.error_message || "Confirmation failed");
    return res.data;
  };

  const onlineSuccess = (ref) => {
    sessionStorage.removeItem("pharmacy_cart");
    sessionStorage.removeItem("pharmacy_slot");
    showToast({ title: "Order confirmed", id: "success" });
    navigate(`/pharmacy/track/${ref}`, { replace: true });
  };

  const steps = [
    { n: 1, label: "Medicines", to: "/pharmacy", done: true },
    { n: 2, label: "Slot", to: "/pharmacy/slots", done: true },
    { n: 3, label: "Address & Payment", done: false, current: true },
  ];

  return (
    <div className="width-container py-6 pb-12">
      {/* Steps */}
      <div className="flex flex-wrap items-center gap-2 mb-6 text-sm overflow-x-auto">
        {steps.map((s, i) => (
          <span key={s.n} className="flex items-center gap-2">
            {i > 0 && <span className="text-gray-300">—</span>}
            {s.done ? (
              <Link to={s.to} className="flex items-center gap-1.5 text-rm-primary font-medium">
                <span className="w-6 h-6 rounded-full bg-rm-primary text-white text-xs flex items-center justify-center">✓</span>
                {s.label}
              </Link>
            ) : (
              <span className="flex items-center gap-1.5 font-semibold text-gray-900">
                <span className="w-6 h-6 rounded-full bg-gray-900 text-white text-xs flex items-center justify-center">{s.n}</span>
                {s.label}
              </span>
            )}
          </span>
        ))}
      </div>

      <h1 className="text-2xl font-bold mb-1">Confirm pharmacy order</h1>
      <p className="text-sm text-gray-500 mb-6">Pin your location on the map, verify the auto-filled address, then pay.</p>

      <div className="grid lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 space-y-5">

          {/* Address card */}
          <section ref={addressCardRef} className="bg-white border rounded-2xl p-5 shadow-sm scroll-mt-24">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg flex items-center gap-2">
                <RiMapPinLine className="text-rm-primary" /> Delivery address
              </h2>
              {geoLoading && <span className="text-xs text-gray-500 flex items-center gap-1"><RiRefreshLine className="animate-spin" /> Locating…</span>}
            </div>

            {/* Saved addresses */}
            {savedAddresses.length > 0 && (
              <div className="space-y-2 mb-4">
                {savedAddresses.map((a) => (
                  <label key={a.id} className={`flex items-start gap-3 border rounded-xl p-3 cursor-pointer transition ${selectedSavedId === a.id && !showNewForm ? "border-rm-primary ring-1 ring-rm-primary bg-teal-50/50" : "hover:border-gray-300"}`}>
                    <input
                      type="radio"
                      name="saved-address"
                      checked={selectedSavedId === a.id && !showNewForm}
                      onChange={() => { setSelectedSavedId(a.id); setShowNewForm(false); applySavedAddress(a); }}
                      className="mt-1 accent-[#0d9488]"
                    />
                    <span className="text-sm">
                      <span className="font-medium">{a.address_as || "Address"}</span>
                      <span className="block text-gray-600">{[a.house_no, a.address].filter(Boolean).join(", ")}</span>
                    </span>
                  </label>
                ))}
                <button
                  type="button"
                  onClick={() => { setShowNewForm(true); setSelectedSavedId(null); }}
                  className={`text-sm font-medium ${showNewForm ? "text-gray-400" : "text-rm-primary underline"}`}
                >
                  + Use a new address
                </button>
              </div>
            )}

            {(showNewForm || savedAddresses.length === 0) && (
              <>
                {/* Map picker */}
                <div className="rounded-xl overflow-hidden border bg-gray-100 relative mb-3">
                  {!mapKey || !isLoaded ? (
                    <p className="p-6 text-center text-sm text-gray-500">{mapKey ? "Loading map…" : "Map unavailable — type your address below."}</p>
                  ) : (
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "260px" }}
                      center={pin}
                      zoom={15}
                      options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                      onClick={(e) => movePin(e.latLng.lat(), e.latLng.lng())}
                    >
                      <Marker
                        position={pin}
                        draggable
                        onDragEnd={(e) => movePin(e.latLng.lat(), e.latLng.lng())}
                      />
                    </GoogleMap>
                  )}
                  <button
                    type="button"
                    onClick={useMyLocation}
                    className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white shadow-lg rounded-lg px-3 py-2 text-xs font-medium hover:bg-gray-50"
                  >
                    <RiCrosshairLine size={16} className="text-rm-primary" /> Use my location
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-4">Tap the map or drag the pin — the address below auto-fills. You can edit every field.</p>

                {/* Editable fields */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Receiver name</label>
                    <input className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d9488]" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone *</label>
                    <input className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d9488]" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" inputMode="tel" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">House / Flat no.</label>
                    <input className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d9488]" value={form.house} onChange={(e) => set("house", e.target.value)} placeholder="e.g. 8-2-293, Road No. 12" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Landmark (optional)</label>
                    <input className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d9488]" value={form.landmark} onChange={(e) => set("landmark", e.target.value)} placeholder="Near …" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Street address * <span className="font-normal text-gray-400">(auto-filled from map — editable)</span></label>
                    <textarea className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d9488]" rows={3} value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="Start typing or pick a point on the map" />
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600 mr-1">Save as:</span>
                    {["Home", "Work", "Other"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => set("saveAs", t)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${form.saveAs === t ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
                      >
                        {t}
                      </button>
                    ))}
                    <button type="button" onClick={saveAddressToAccount} className="ml-auto text-xs text-rm-primary underline">
                      Save to my addresses
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Prescription notice */}
          {rxCount > 0 && (
            <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
              <strong>Prescription required</strong> for {rxCount} item(s). Keep it ready — the pharmacist may call {form.phone || "you"} to verify before dispatch.
            </section>
          )}

          {/* Coupon card */}
          <section className="bg-white border rounded-2xl p-5 shadow-sm">
            <h2 className="font-semibold mb-3">Have a coupon?</h2>
            <div className="flex gap-2">
              <input className="flex-1 border rounded-lg px-3 py-2.5 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#0d9488]" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="WELCOME10" />
              {coupon && (
                <button type="button" onClick={() => setCoupon("")} className="px-3 text-gray-400 hover:text-gray-600" aria-label="Clear coupon">
                  <RiCloseLine size={18} />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">First order? <strong>WELCOME10</strong> is pre-applied. Discount is confirmed at payment.</p>
          </section>
        </div>

        {/* Summary sidebar */}
        <aside className="bg-white border rounded-2xl p-5 shadow-sm lg:sticky lg:top-24">
          <h2 className="font-semibold mb-3">Order summary</h2>
          <ul className="space-y-2 max-h-56 overflow-auto pr-1 mb-4">
            {cart.map((c) => (
              <li key={c.medicine_id} className="flex items-start justify-between gap-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate font-medium">{c.name}</span>
                  {c.requires_prescription && <span className="text-[11px] text-amber-700">Rx required</span>}
                </span>
                <span className="text-gray-500 flex-shrink-0">× {c.quantity}</span>
              </li>
            ))}
          </ul>
          <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 mb-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Delivery</span>
              <span className="font-medium">{formatDateLabel(slot.delivery_date)} · {String(slot.delivery_slot).replace("-", "–")}</span>
            </div>
            <Link to="/pharmacy/slots" className="text-xs text-rm-primary underline mt-1 inline-block">Change slot</Link>
          </div>
          <dl className="text-sm space-y-1.5 border-t pt-3 mb-4">
            <div className="flex justify-between"><dt className="text-gray-600">Items</dt><dd className="font-medium">{cart.length} · Qty {totalQty}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-600">Delivery</dt><dd className="font-medium text-green-700">Calculated at payment</dd></div>
            {coupon && <div className="flex justify-between"><dt className="text-gray-600">Coupon</dt><dd className="font-medium">{coupon}</dd></div>}
          </dl>
          {!uid && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">You’ll be asked to log in before payment — your address entries are kept.</p>}
          {!onlineFlow ? (
            <button type="button" className="rm-btn-primary w-full py-3 text-base font-semibold" onClick={startOnline}>
              Continue to payment
            </button>
          ) : (
            <V1CashfreeCheckout
              amount="—"
              createCheckout={createOnlineCheckout}
              confirmPayment={confirmOnline}
              onSuccess={onlineSuccess}
              onCancel={() => setOnlineFlow(false)}
            />
          )}
          <p className="text-[11px] text-gray-400 text-center mt-3">Secure payment via Cashfree · UPI, cards & netbanking</p>
        </aside>
      </div>
    </div>
  );
}